import { useState } from 'react';
import { Moon, Sun, Search, HelpCircle, Mail, Star, Menu, Users, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { XLogo } from '@/components/icons/x-logo';
import { useTheme } from '@/hooks/use-theme';
import { useAuth } from '@/hooks/use-auth';
import { validateGitHubUrl, analyzeRepository } from '@/lib/analyze';
import { useToast } from '@/hooks/use-toast';
import { AuthDialog } from '@/components/auth/auth-dialog';
import { ContactDialog } from '@/components/contact/contact-dialog';
import { FAQDialog } from '@/components/faq/faq-dialog';
import { Toaster } from '@/components/ui/toaster';
import { HyperText } from '@/components/ui/hyper-text';

export default function App() {
  // State management
  const [url, setUrl] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showFAQDialog, setShowFAQDialog] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const { toast } = useToast();

  // Handle repository analysis
  const handleAnalyze = async () => {
    if (!user) {
      setShowAuthDialog(true);
      return;
    }

    if (!validateGitHubUrl(url)) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid GitHub repository URL",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzing(true);
    try {
      const { error } = await analyzeRepository(url, user.id);
      if (error) {
        toast({
          title: error.includes('Please wait') ? "Rate Limited" : "Analysis Failed",
          description: error,
          variant: "destructive"
        });
      } else {
        toast({
          title: "You've got an email with the results!",
          description: "Check your inbox for the analysis report."
        });
        setUrl('');
      }
    } catch (error) {
      toast({
        title: "Analysis Failed",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-primary">
      {/* Header Section - CRITICAL: Keep all navigation items */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-header-bg backdrop-blur supports-[backdrop-filter]:bg-header-bg/60">
        <div className="container max-w-7xl mx-auto flex h-14 sm:h-16 items-center px-4 sm:px-8">
          <div className="flex items-center space-x-4 flex-1">
            <div className="flex items-center space-x-3">
              <img 
                src={theme === 'dark' ? '/logo_wt.svg' : '/logo_bk.svg'} 
                alt="RepoScan.ai Logo" 
                className="h-8 w-auto"
              />
              <span className="font-semibold">RepoScan.ai</span>
            </div>
            <nav className="hidden md:flex space-x-4 ml-8">
              <Button variant="ghost" className="text-text-secondary hover:text-text-primary">
                <Search className="h-4 w-4 mr-2" />
                Explore
              </Button>
              <Button 
                variant="ghost" 
                className="text-text-secondary hover:text-text-primary"
                onClick={() => setShowFAQDialog(true)}
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                FAQ
              </Button>
              <Button 
                variant="ghost" 
                className="text-text-secondary hover:text-text-primary"
                onClick={() => setShowContactDialog(true)}
              >
                <Mail className="h-4 w-4 mr-2" />
                Contact
              </Button>
            </nav>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Button 
              variant="ghost" 
              onClick={toggleTheme}
              size="sm"
              className="text-text-primary hover:bg-background-secondary"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button 
              variant="outline"
              size="sm"
              className="border-border hover:border-border hover:bg-background-secondary hidden sm:inline-flex"
              onClick={user ? signOut : () => setShowAuthDialog(true)}
            >
              {user ? 'Sign out' : 'Sign in'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Mobile menu - CRITICAL: Must match desktop navigation items */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border bg-header-bg">
            <nav className="flex flex-col p-4">
              <Button variant="ghost" className="justify-start text-text-secondary hover:text-text-primary">
                <Search className="h-4 w-4 mr-2" />
                Explore
              </Button>
              <Button 
                variant="ghost" 
                className="justify-start text-text-secondary hover:text-text-primary"
                onClick={() => setShowFAQDialog(true)}
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                FAQ
              </Button>
              <Button 
                variant="ghost" 
                className="justify-start text-text-secondary hover:text-text-primary"
                onClick={() => setShowContactDialog(true)}
              >
                <Mail className="h-4 w-4 mr-2" />
                Contact
              </Button>
              <Button 
                variant="outline"
                className="mt-2 border-border hover:border-border hover:bg-background-secondary"
                onClick={user ? signOut : () => setShowAuthDialog(true)}
              >
                {user ? 'Sign out' : 'Sign in'}
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content Section */}
      <main className="flex-grow flex items-center py-8 sm:py-12">
        <div className="container max-w-7xl mx-auto px-4 sm:px-8">
          <div className="mx-auto max-w-2xl text-center">
            {/* Title Section - CRITICAL: Maintain proper spacing and line breaks */}
            <div className="mb-4">
              <div className="md:hidden">
                <HyperText
                  text="AI-POWERED"
                  className="text-xl sm:text-2xl font-bold tracking-tight text-text-primary"
                  duration={1000}
                  animateOnLoad={true}
                />
                <HyperText
                  text="GITHUB REPO ANALYSIS"
                  className="text-xl sm:text-2xl font-bold tracking-tight text-text-primary"
                  duration={1000}
                  animateOnLoad={true}
                />
              </div>
              <div className="hidden md:block">
                <HyperText
                  text="AI-POWERED GITHUB REPOSITORY ANALYSIS"
                  className="text-3xl font-bold tracking-tight text-text-primary"
                  duration={1000}
                  animateOnLoad={true}
                />
              </div>
            </div>
            
            {/* Description - CRITICAL: Keep text alignment and spacing */}
            <p className="mb-8 text-text-secondary text-sm sm:text-base px-4">
              Our advanced AI analyzes repositories in real-time, providing code quality scores, 
              security checks, and a clear PASS or FAIL verdict to ensure production readiness.
            </p>
            
            {/* Analysis Input Section */}
            <div className="flex flex-col space-y-4 px-4">
              <Input
                type="text"
                placeholder="Enter a GitHub repository URL..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-12 bg-search-bg border-border text-base"
              />
              <Button 
                className="h-12 bg-accent-primary hover:bg-accent-hover text-button-text font-semibold"
                onClick={handleAnalyze}
                disabled={isAnalyzing}
              >
                {isAnalyzing ? 'Analyzing...' : 'Analyze Repository'}
              </Button>
            </div>

            {/* Stats Section - CRITICAL: Maintain icon alignment */}
            <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-text-secondary text-sm">
              <div className="flex items-center justify-center">
                <Star className="h-5 w-5 mr-2" />
                <span>10k+ Repos Analyzed</span>
              </div>
              <div className="flex items-center justify-center">
                <Users className="h-5 w-5 mr-2" />
                <span>5k+ Users</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Section - CRITICAL: Keep disclaimer text */}
      <footer className="border-t border-border py-6">
        <div className="container max-w-7xl mx-auto px-4 sm:px-8 text-center text-text-secondary">
          <div className="flex justify-center space-x-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-text-secondary hover:text-text-primary"
              asChild
            >
              <a
               href="https://x.com/reposcans"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on X (Twitter)"
              >
                <XLogo className="h-4 w-4" />
              </a>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-text-secondary hover:text-text-primary"
              asChild
            >
              <a
                href="https://t.me/reposcan"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Join our Telegram channel"
              >
                <Send className="h-4 w-4" />
              </a>
            </Button>
          </div>
          <p className="mb-2 text-sm">© 2025 RepoScan.ai - All rights reserved</p>
          <p className="text-xs text-text-secondary/80 px-4">
            Disclaimer: Results are for informational purposes only and not guaranteed for accuracy. 
            Use at your own risk. We are not liable for any actions taken based on these results.
          </p>
        </div>
      </footer>

      {/* Auth Dialog - CRITICAL: Required for user authentication */}
      <AuthDialog 
        isOpen={showAuthDialog} 
        onClose={() => setShowAuthDialog(false)} 
      />
      <ContactDialog
        isOpen={showContactDialog}
        onClose={() => setShowContactDialog(false)}
      />
      <FAQDialog
        isOpen={showFAQDialog}
        onClose={() => setShowFAQDialog(false)}
      />
      <Toaster />
    </div>
  );
}